import * as XLSX from 'xlsx';
import { useState, useRef } from 'react';
import { Button } from '@mui/material';
import BasicModal from '../Modal/BasicModal'
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import styled from '@mui/system/styled';
import basketService from '../../../api/basket';
import useI18n from '../../../hooks/useI18n';
import { useRecoilState } from "recoil";
import { basketState } from '../../../state/basketState';
import { updateBasketState } from '../../../state/updateBasketState';

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});


function ExcelImport() {
    const [modalOpen, setModalOpen] = useState(false);
    const [modalContent, setModalContent] = useState({
        type: 'info',
        title: '',
        text: ''
    });
    const fileInputRef = useRef(null);

    const [basketRecoil, setBasketRecoil] = useRecoilState(basketState);
    const [update, setUpdate] = useRecoilState(updateBasketState);

    const { lang, l } = useI18n();

    const handleFileUpload = (e) => {
        const file = e.target.files[0];

        // Validate file extension
        if (!file || !/\.(xlsx|xls)$/i.test(file.name)) {
            setModalContent({
                type: 'error',
                title: l('File not supported'),
                text: l('Please select an Excel file (.xlsx, .xls).'),
            });
            setModalOpen(true);
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }

        const reader = new FileReader();
        reader.onload = (evt) => {
            let wb;
            try {
                const bstr = evt.target.result;
                wb = XLSX.read(bstr, { type: 'binary' });
            } catch (err) {
                setModalContent({
                    type: 'error',
                    title: l('Error'),
                    text: l('The file could not be read. Please check that it is correct.'),
                });
                setModalOpen(true);
                if (fileInputRef.current) fileInputRef.current.value = '';
                return;
            }
            // Перевірка чи є листи
            if (!wb.SheetNames || wb.SheetNames.length < 1) {
                setModalContent({
                    type: 'error',
                    title: l('Empty file'),
                    text: l('The file does not contain any data.'),
                });
                setModalOpen(true);
                if (fileInputRef.current) fileInputRef.current.value = '';
                return;
            }

            let result = {};

            for (const sheetName of wb.SheetNames) {
                const ws = wb.Sheets[sheetName];
                // Дістаємо дані як масив рядків, при цьому не ігноруємо порожні
                const sheetData = XLSX.utils.sheet_to_json(ws, {
                    header: 1,
                    defval: '', // щоб пусті клітини теж були
                    raw: false
                });

                // Перевіряємо, чи є хоча б 9 рядків
                if (sheetData.length < 9) continue;

                for (let i = 8; i < sheetData.length; i++) { // з 0, тобто 9-а рядок це індекс 8
                    const row = sheetData[i];
                    // стовпці B і H, тобто індекси 1 і 7
                    let qtartcode = row[1] ? (row[1] + '').trim() : '';
                    let quantRaw = row[7];

                    // Якщо немає штрихкоду пропускаємо такий рядок
                    if (!qtartcode) continue;

                    // quant: якщо пусто чи не число -- 0
                    let quant = parseFloat(quantRaw?.toString().replace(',', '.'));
                    if (isNaN(quant)) quant = 0;

                    // якщо quant вже є -- сумуємо
                    if (result[qtartcode]) {
                        result[qtartcode] += quant;
                    } else {
                        result[qtartcode] = quant;
                    }
                }
            }

            // Якщо пусто то виводимо помилку
            if (Object.entries(result).length === 0) {
                setModalContent({
                    type: 'error',
                    title: l('Data not found'),
                    text: l('No records were found from row 9 in columns B and H.'),
                });
                setModalOpen(true);
                if (fileInputRef.current) fileInputRef.current.value = '';
                return;
            }

            const arr = Object.entries(result).map(([qtartcode, quant]) => ({
                qtartcode,
                quant
            }))
                .filter(obj => obj.quant !== 0); // Видаляємо об'єкти, де quant == 0

            // Якщо пусто то виводимо помилку
            if (arr.length === 0) {
                setModalContent({
                    type: 'error',
                    title: l('Data not found'),
                    text: l('No records found. Make sure you entered the quantity in the Order column.'),
                });
                setModalOpen(true);
                if (fileInputRef.current) fileInputRef.current.value = '';
                return;
            }

            // Консоль для перевірки
            // console.log('Результат імпорту:', arr);

            basketService.updateListBaskets(arr)
                .then(response => {
                    setBasketRecoil(response);
                    setUpdate({ BoolUpdate: !update.BoolUpdate });
                    setModalContent({
                        type: 'success',
                        title: l('Import successful'),
                        text: l('The data has been successfully imported.')
                    });
                    setModalOpen(true);
                })
                .catch(error => {
                    console.error("❌ Помилка при імпорті:", error);
                    setModalContent({
                        type: 'danger',
                        title: l('Import failed'),
                        text: l('An error occurred while importing the data.')
                    });
                    setModalOpen(true);
                });

            if (fileInputRef.current) fileInputRef.current.value = '';
        };

        reader.onerror = () => {
            setModalContent({
                type: 'error',
                title: l('Reading error'),
                text: l('Could not read file!'),
            });
            setModalOpen(true);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }

        reader.readAsBinaryString(file);
    };

    return (
        <div>
            <BasicModal
                isOpened={modalOpen}
                handle={() => setModalOpen(false)}
                text={modalContent.text}
                title={modalContent.title}
                type={modalContent.type}
            />

            <Button
                component="label"
                role={undefined}
                variant="contained"
                tabIndex={-1}
                startIcon={<CloudUploadIcon />}
                style={{ marginTop: 10, width: '100%', textAlign: 'center' }}
            >
                {l('Remains of RybRay excel')}
                <VisuallyHiddenInput
                    accept=".xlsx,.xls"
                    type="file"
                    onChange={handleFileUpload}
                    multiple={false}
                    ref={fileInputRef}
                />
            </Button>
        </div>
    );
}

export default ExcelImport;